import {NumberInput, Slider} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';

import {suffixTestId, type Nullish} from 'shared/web';

import {FILTER_VALUE_KEY} from '../../constants/filterValueKey';
import type {EagFilterProps} from '../../types/EagFilterProps';
import {calculateSliderTicks} from '../../utils/calculateSliderTicks';
import {getFileSizeUnit} from '../../utils/getFileSizeUnit';
import {useRangeSliderFilter} from '../NumberSliderRangeFilter/hooks/useRangeSliderFilter';
import {QuickFilterChips} from '../QuickFilterChips/QuickFilterChips';

type FileSizeSliderFilterProps = EagFilterProps & {
  min: number;
  max: number;
  minOpen: boolean;
  maxOpen: boolean;
  stepSize: number | null;
  steps: number[];
  markerSize: number | null;
  markers: number[];
  inputsVisible: boolean;
  quickFilters?: Array<{value: string; label: string}>;
};

const DISPLAY_DECIMALS = 2;
const DISPLAY_MULTIPLIER = 10 ** DISPLAY_DECIMALS;
const roundForDisplay = (v: number) => Math.round(v * DISPLAY_MULTIPLIER) / DISPLAY_MULTIPLIER;

export function FileSizeSliderFilter(props: FileSizeSliderFilterProps) {
  const {label: unitLabel, factor: unitFactor} = getFileSizeUnit(props.max);

  const ticks = calculateSliderTicks(props.min, props.max, props.markerSize, props.markers);

  const model = props.value[FILTER_VALUE_KEY] as [number | null, number | null] | string | Nullish;

  const formatValue = (value: number) => `${roundForDisplay(value / unitFactor)} [${unitLabel}]`;
  const bytesToUnit = (bytes: number) => roundForDisplay(bytes / unitFactor);
  const unitToBytes = (value: number) => Math.round(value * unitFactor);

  const filter = useRangeSliderFilter({
    model,
    onModelChange: (newValue) => props.onChange({[FILTER_VALUE_KEY]: newValue}),
    min: props.min,
    max: props.max,
    valueTransforms: {toDisplay: bytesToUnit, fromDisplay: unitToBytes},
  });

  return (
    <VStack spacing={5}>
      <QuickFilterChips
        quickFilters={props.quickFilters}
        value={filter.quickFilterValue}
        defaultValue={null}
        setFilterValue={filter.applyQuickFilter}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('file-size-filter-quickFilters', props)}
      />
      <Slider
        isDisabled={props.isDisabled}
        isRange
        min={props.min}
        max={props.max}
        step={props.stepSize ?? (props.steps?.length ? props.steps : 1)}
        value={filter.sliderValue}
        allowMoveStart
        onChange={filter.onSliderChange}
        formatValue={formatValue}
        ticks={ticks}
        onlyEdges={true}
        displayValue="auto"
        data-testid={suffixTestId('file-size-filter-slider', props)}
      />
      <HStack spacing={2}>
        {/* minStepperValue is OMITTED while a bound is empty so the platform
            stepper emits ±step artifacts — the only signal that lets
            useRangeSliderFilter tell plus apart from minus (empty From − →
            min, empty To + → max). Once a value exists it is passed again so
            the platform clamps at min (same contract as
            NumberSliderRangeFilter). */}
        <NumberInput
          value={filter.fromValue}
          onChange={filter.onFromChange}
          isStepperVisible
          minStepperValue={filter.fromValue !== null ? bytesToUnit(props.min) : undefined}
          maxStepperValue={bytesToUnit(props.max)}
          isDisabled={props.isDisabled}
          data-testid={suffixTestId('file-size-filter-from', props)}
        />
        <NumberInput
          value={filter.toValue}
          onChange={filter.onToChange}
          isStepperVisible
          minStepperValue={filter.toValue !== null ? bytesToUnit(props.min) : undefined}
          maxStepperValue={bytesToUnit(props.max)}
          isDisabled={props.isDisabled}
          data-testid={suffixTestId('file-size-filter-to', props)}
        />
      </HStack>
    </VStack>
  );
}
